/**
 * Money Market Fund Symbol Patterns
 * 
 * Patterns for identifying money market funds for income estimation.
 */

import type { SymbolPattern } from '../types.js';

/**
 * Known money market fund symbols
 */
export const MONEY_MARKET_SYMBOLS = [
  // Vanguard
  'VMFXX',  // Vanguard Federal Money Market Fund
  'VUSXX',  // Vanguard Treasury Money Market Fund
  'VMSXX',  // Vanguard Municipal Money Market Fund
  'VMMXX',  // Vanguard Prime Money Market Fund
  // Schwab
  'SNSXX',  // Schwab U.S. Treasury Money Fund
  'SWVXX',  // Schwab Value Advantage Money Fund (prime)
  'SNVXX',  // Schwab Government Money Fund
  // Fidelity
  'SPAXX',  // Fidelity Government Money Market Fund
  'FDRXX',  // Fidelity Government Cash Reserves
  'FZFXX',  // Fidelity Treasury Money Market Fund (repo-heavy)
  'FDLXX',  // Fidelity Treasury Only Money Market Fund
  'SPRXX',  // Fidelity Money Market Fund (prime)
];

/**
 * Money market description keywords
 */
export const MONEY_MARKET_KEYWORDS = [
  'money market',
  'money fund',
  'money investor',
  'cash reserve', // matches both "cash reserve" and "cash reserves"
  'government cash',
  'treasury money',
  'prime money',
  'prime advantage',
  'liquid reserve',
  'sweep',
  'settlement fund',
];

/**
 * Money market patterns
 */
export const MONEY_MARKET_PATTERNS: SymbolPattern[] = [
  // Known symbols
  ...MONEY_MARKET_SYMBOLS.map(sym => ({
    pattern: new RegExp(`^${sym}$`, 'i'),
    description: `Money Market Fund: ${sym}`,
  })),
  // Symbols ending in XX (common money market convention). Requires at
  // least 5 characters so a bare "XX" isn't flagged — real MMF tickers
  // (VMFXX, SPAXX, etc.) are 5 characters.
  { pattern: /^.{3,}XX$/i, description: 'Symbol ends with XX (5+ chars)' },
  // Description patterns
  ...MONEY_MARKET_KEYWORDS.map(keyword => ({
    pattern: new RegExp(keyword, 'i'),
    description: `Contains "${keyword}"`,
  })),
];

/**
 * Check if a symbol or description indicates a money market fund
 */
export function isMoneyMarketSymbol(symbol: string, description?: string): boolean {
  const sym = symbol?.toUpperCase() || '';
  const desc = description || '';
  
  // Known money market symbols
  if (MONEY_MARKET_SYMBOLS.includes(sym)) {
    return true;
  }
  
  // Symbols ending in XX (5+ characters — see MONEY_MARKET_PATTERNS above)
  if (sym.length >= 5 && sym.endsWith('XX')) {
    return true;
  }
  
  // Description keywords
  if (MONEY_MARKET_KEYWORDS.some(kw => desc.toLowerCase().includes(kw.toLowerCase()))) {
    return true;
  }
  
  return false;
}
